<template>
	<div v-if="restaurant">
		<el-row id="nav-restaurant">
			<el-col :span="3" class="text-right" style="line-height: 75px;">
				<img v-if="$i18n.locale == 'en'" @click="toggleLanguage('th')" class="fs-nav-icon pointer" width="26" height="26" src="@/assets/icons/en_no_background.png"/>
				<img v-else @click="toggleLanguage('en')" class="fs-nav-icon pointer" width="26" height="26" src="@/assets/icons/th_no_background.png"/>
			</el-col>
		</el-row>
		<RestaurantStory 
			:restaurant="restaurant" 
			:loading="loading" 
			class="pb--4"
		/>
		<el-divider class="color-whitesmoke overide-divider" />
		<RestaurantDetail 
			@toggle_all_branch="toggle_all_branch" 
			:nearbyBranch="nearbyBranch"
			:loading="loading"
		/>
		<el-divider class="color-whitesmoke overide-divider" />
		<RestaurantImage 
			@toggle_all_image="toggle_all_image" 
			:restaurantImage="restaurant.restaurantImage"
			:loading="loading"
		/>
		<div class="p-sticky bottom-0 px--4 py--6 color-white">
			<BaseButton 
				@click="goToBranch"
				:loading="loading"
				:disabled="!branches.length"
				class="color-primary color-white--text full-width"
			>
				{{ $t('pageRestaurant.orderFood') }}
			</BaseButton>
		</div>
		<RestaurantBranch
			v-model="isBranchSelectionDrawerOpen" 
			v-if="!loading"
			:from-location="selectedLocation"
			:branches="branches"
		/>
		<RestauranMoreImage 
			v-model="isImageDrawerOpen" 
			@toggle_all_image="toggle_all_image" 
			:images="restaurant.restaurantImage"
		/>

		<!-- Location -->
		<LocationSettingDrawer 
			v-model="isLocationSettingDrawerOpen"
			@use:current="useCurrentLocation"
			@use:custom="openLocationSelectionDrawer"
		/>
		
		<LocationSelectionDrawer 
			v-model="isLocationSelectionDrawerOpen" 
			:location="selectedLocation || nearbyBranchLocation"
			@submit="onSelectLocation"
		/>
	</div>
</template>

<script>
import LocationUtil from "@/utils/location"

import BaseButton from '@/baseComponents/button/BaseButton.vue'
import RestaurantStory from "@/components/restaurant/RestaurantStory";
import RestaurantDetail from "@/components/restaurant/RestaurantDetail";
import RestaurantImage from "@/components/restaurant/RestaurantImage";
import RestaurantBranch from "@/components/restaurant/RestaurantBranch";
import RestauranMoreImage from "@/components/restaurant/RestauranMoreImage";

import LocationSettingDrawer from '@/components/locationsetting/LocationSettingDrawer.vue'
import LocationSelectionDrawer from '@/components/locationselection/LocationSelectionDrawer.vue'

export default {
	name: 'RestaurantPage',
	components: {
		RestaurantStory,
		RestaurantDetail,
		RestaurantImage,
		RestaurantBranch,
		RestauranMoreImage,
		BaseButton,
		LocationSettingDrawer,
		LocationSelectionDrawer
	},
	data() {
		return {
			isBranchSelectionDrawerOpen: false,
			isLocationSettingDrawerOpen: false,
			isLocationSelectionDrawerOpen: false,
			isImageDrawerOpen: false,
			selectedLocation: null,
			loading: false,
			branches: [],
			nearbyBranch: {}
		}
	},
	mounted: async function() {
		return this.load(async () => {
			await this.waitRestaurant()

			const userLocation = JSON.parse(localStorage.getItem('fsloc') || '{}')
			const hasLocation = Number.isFinite(userLocation.lat) && Number.isFinite(userLocation.lng)
			if (hasLocation) {
				this.selectedLocation = userLocation
			} else {
				this.isLocationSettingDrawerOpen = true 
			}
			this.branches = await	this.mapHaversineDistanceIntoEachBranch(this.onlineBranches)
			await this.updateNearbyBranch(this.branches)
		})
	},
	methods: {
		load: async function(func) {
			try {
				this.loading = true
				const result = await func()
				return result
			} finally {
				this.loading = false
			}
		},
		waitRestaurant: async function() {
			if (!this.restaurant) {
				await new Promise(resolve => setTimeout(resolve, 100))
				return this.waitRestaurant()
			}
		},
		calculateDistance: function(from, branch) {
			return  this.$store.dispatch('calculateDistance', {
				origin_coordinates: [branch.latitude, branch.longitude],
				origin_address: branch.address,
				destination_address: from.address,
				destination_coordinates: [from.lat, from.lng]
			})
		},
		updateNearbyBranch: async function(branches) {
			if (!branches.length) return this.nearbyBranch = {}

			const branchesSorted = branches.slice().sort((a, b) => a.distance - b.distance)
			const firstBranch = branchesSorted[0]

			if (this.selectedLocation) {
				const distance = await this.calculateDistance(this.selectedLocation, firstBranch)
				return this.nearbyBranch = Object.assign({}, firstBranch, { distance })
			}

			const masterBranch = branchesSorted.find(item => item.master_branch_id)
			this.nearbyBranch = Object.assign({}, masterBranch || firstBranch)
		},
		updateLocation: async function(location) {
			this.selectedLocation = location
			localStorage.setItem('fsloc', JSON.stringify(Object.assign({}, this.selectedLocation, { new: 1 })))
			return this.load(async () => {
				this.branches = await	this.mapHaversineDistanceIntoEachBranch(this.onlineBranches)
				await this.updateNearbyBranch(this.branches)
			})
		},
		useCurrentLocation: function(location) {
			return this.updateLocation(location)
		},
		openLocationSelectionDrawer: function() {
			this.isLocationSelectionDrawerOpen = true
		},
		onSelectLocation: function({ location, done }) {
			this.updateLocation(location)
			done(true)
		},
		isOnlyOneFlag (branch) {
			return (+branch.delivery_flag+ +branch.pickup_flag) == 2
		},
		toggle_all_branch() {
			this.isBranchSelectionDrawerOpen = !this.isBranchSelectionDrawerOpen
		},
		toggle_all_image() {
			this.isImageDrawerOpen = !this.isImageDrawerOpen
		},
		toggleLanguage (lang) {
			this.$store.dispatch('setLanguage', lang)
		},
		goToBranch: function() {
			if (!this.branches.length) return
			const hasOneBranch = this.branches.length === 1
			if (hasOneBranch) {
				const { branch_alias, branch_uuid } = this.branches[0]
				const branch = { uuid: branch_uuid, alias: branch_alias || branch_uuid }
				localStorage.setItem('branch', JSON.stringify(branch))
				return this.$router.push(this.$route.path + "/" + branch.alias)
			}
			return this.toggle_all_branch()
		},
		mapHaversineDistanceIntoEachBranch: async function(branches) {
			if (!this.selectedLocation) return branches
			const branchesMapped = await Promise.all(branches.map(async (branch) => {
				// const distance = await this.$store.dispatch('calculateDistance', {
				// 	origin_coordinates: [branch.latitude, branch.longitude],
				// 	origin_address: branch.address,
				// 	destination_address: this.selectedLocation.address,
				// 	destination_coordinates: [this.selectedLocation.lat, this.selectedLocation.lng]
				// })
				const branchLocation = { lat: branch.latitude, lng: branch.longitude }
				const distanceInMeter = LocationUtil.getDistanceByHaversine(branchLocation,this.selectedLocation)
				return Object.assign({}, branch, { distance: distanceInMeter })
			}))
			return branchesMapped
		}
	},
	computed: {
		nearbyBranchLocation: function() {
			const { latitude, longitude } = this.nearbyBranch || {}
			const hasLocation = Number.isFinite(latitude) && Number.isFinite(longitude)
			return hasLocation ? { lat: latitude, lng: longitude } : {}
		},
		onlineBranches: function() {
			const branches = this.restaurant && this.restaurant.branches ? this.restaurant.branches : []
			return branches.filter(({ display_online_flag = '' }) => display_online_flag == 1)
		},
		restaurant() {
			return this.$store.getters.RestaurantData
		},
		// nearbyBranch: function() {
		// 	const branches = this.branches.sort((a, b) => a.distance - b.distance)

		// 	if (this.selectedLocation) return Object.assign({}, branches[0])

		// 	const master_branch_id = _.get(_.compact(branches.map(item => item.master_branch_id)), 0)
		// 	if (master_branch_id) {
		// 		const master_branch = branches.find(item => item.branch_id == master_branch_id)
		// 		return master_branch || branches[0]
		// 	}

		// 	return  branches[0]
		// }
	},
}
</script>

<style lang="scss" scoped>
.overide-divider.el-divider--horizontal {
	height: 8px;
	margin: 0;
}
</style>


// WEBPACK FOOTER //
// src/components/restaurant/RestaurantPage.vue